import { inject } from '@angular/core';
import { type CanMatchFn, Router, type UrlTree } from '@angular/router';
import { AuthService, SIGN_IN_PATH } from '../auth/auth.service';
import { SessionStore } from '../auth/session.store';
import { FeatureFlagService } from '../features/feature-flag.service';
import { PermissionService } from '../permissions/permission.service';
import { type PermissionRequirement } from '../permissions/permission-catalog';

/**
 * Route guards. All are `canMatch`, so a route the user may not enter never downloads its
 * lazy chunk. They share one rule: wait for the session to resolve, send an anonymous user
 * to sign in with the page they wanted as `returnUrl`, and send a signed-in user who lacks
 * access somewhere they can understand.
 */

/** Where a user with a default password is held until they choose their own. */
export const CHANGE_PASSWORD_PATH = '/account/change-password';

/**
 * Resolves the session, then returns a redirect for an anonymous user or null to continue.
 * A signed-in user whose account is flagged "must change password" (a new user holding the
 * default password) is sent to the change-password page for every other route.
 */
async function requireSession(): Promise<UrlTree | null> {
  const auth = inject(AuthService);
  const router = inject(Router);
  const store = inject(SessionStore);
  await auth.restore();
  const target = router.getCurrentNavigation()?.extractedUrl;
  if (store.isAuthenticated()) {
    if (!store.user()?.mustChangePassword) return null;
    const path = target ? router.serializeUrl(target).split(/[?#]/)[0] : '';
    return path === CHANGE_PASSWORD_PATH ? null : router.createUrlTree([CHANGE_PASSWORD_PATH]);
  }

  const returnUrl = target ? router.serializeUrl(target) : '';
  return router.createUrlTree([SIGN_IN_PATH], {
    queryParams: returnUrl && returnUrl !== '/' ? { returnUrl } : {},
  });
}

/** Any signed-in user. For screens that belong to the user rather than to a permission. */
export const authGuard: CanMatchFn = async () => (await requireSession()) ?? true;

/**
 * The user must hold the permission, or any one of an array. Typed against the catalog, so
 * a permission that the API does not declare fails to compile.
 */
export function permissionGuard(required: PermissionRequirement): CanMatchFn {
  return async () => {
    // inject() only works before the first await.
    const permissions = inject(PermissionService);
    const router = inject(Router);
    const redirect = await requireSession();
    if (redirect) return redirect;
    return permissions.has(required) || router.createUrlTree(['/forbidden']);
  };
}

/** The flag must be on for the signed-in company. A route behind a flag that is off is a 404. */
export function featureGuard(flag: string): CanMatchFn {
  return async () => {
    const flags = inject(FeatureFlagService);
    const router = inject(Router);
    const redirect = await requireSession();
    if (redirect) return redirect;
    return flags.isOn(flag) || router.createUrlTree(['/not-found']);
  };
}
